import React, { useEffect, useState, useRef, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import { message } from '../utils/antdMessage';
import mapStateManager from '../utils/mapStateManager';
import { applyMapboxToken } from '../utils/mapboxToken';
import { attachCoordAdapter, DATUM_WGS84 } from '../utils/dashboard/basemapCoord';
import { isSecureGeolocationContext, getGeolocationHttpsHint } from '../utils/secureContext';
import {
  DEFAULT_HOMEPAGE_SETTINGS,
  normalizeHomepageSettings,
  pickHomepageDeviceSettings,
  isHomepageMobileView,
  getEarthFitMetrics,
  calcBrandFontSizePx,
  hexToRgba
} from '../utils/homepageSettings';

applyMapboxToken(mapboxgl);

const LONGQUAN_CENTER = [119.13643, 28.07741];
const EARTH_LAT = 20;

/** 首页地球氛围：自然日间感，避免过暗或灰蒙蒙 */
function applyHomepageGlobeLook(map) {
  if (!map) return;
  try {
    map.setProjection('globe');
  } catch (e) {
    /* ignore */
  }

  try {
    // 薄大气、低雾感，减少灰蒙蒙罩层
    map.setFog({
      color: 'rgb(55, 95, 145)',
      'high-color': 'rgb(90, 150, 225)',
      'horizon-blend': 0.02,
      'space-color': 'rgb(6, 12, 26)',
      'star-intensity': 0.45
    });
  } catch (e) {
    /* ignore */
  }

  try {
    // 轻度暖白光照，不要过曝
    map.setLight({
      anchor: 'map',
      color: '#fff8f0',
      intensity: 0.2,
      position: [1.4, 200, 35]
    });
  } catch (e) {
    /* ignore */
  }

  // 轻微提饱和与对比，去掉之前导致发灰的降对比/抬暗部
  try {
    const style = map.getStyle();
    const layers = style?.layers || [];
    layers.forEach((layer) => {
      if (layer.type !== 'raster') return;
      const id = layer.id;
      try {
        map.setPaintProperty(id, 'raster-brightness-min', 0);
        map.setPaintProperty(id, 'raster-brightness-max', 1);
        map.setPaintProperty(id, 'raster-saturation', 0.12);
        map.setPaintProperty(id, 'raster-contrast', 0.06);
      } catch (err) {
        /* ignore per layer */
      }
    });
  } catch (e) {
    /* ignore */
  }
}

/**
 * 首页地图
 * - PC / 手机端独立设置（边距规则不同）
 * - 品牌大字相对地球居中，加载完成后再显示
 */
function HomePage() {
  const mapContainer = useRef(null);
  const mapRef = useRef(null);
  const earthAnimationRef = useRef(null);
  const isEarthSpinningRef = useRef(false);
  const earthZoomRef = useRef(1.4);
  const homepageSettingsRef = useRef(DEFAULT_HOMEPAGE_SETTINGS);
  const locateTriggeredRef = useRef(false);

  const [homepageSettings, setHomepageSettings] = useState(null);
  const [showBrandTitle, setShowBrandTitle] = useState(false);
  const [isEarthReady, setIsEarthReady] = useState(false);
  const [locating, setLocating] = useState(false);
  const [brandFontSize, setBrandFontSize] = useState('18vmin');
  const [earthDiameterPx, setEarthDiameterPx] = useState(280);
  const [brandTextWidthPx, setBrandTextWidthPx] = useState(280);
  const [brandScale, setBrandScale] = useState(1);
  const brandTextRef = useRef(null);
  const brandBoxRef = useRef(null);
  const [isMobileView, setIsMobileView] = useState(
    typeof window !== 'undefined' ? isHomepageMobileView() : false
  );

  const [mapStatus, setMapStatus] = useState({
    zoom: 1,
    center: [0, 20],
    height: 0,
    elevation: null
  });

  const mapStyles = [
    { name: '二维地图', id: 'mapbox://styles/mapbox/streets-zh-v1' },
    { name: '卫星地图', id: 'mapbox://styles/mapbox/satellite-v9' },
    { name: '混合地图', id: 'mapbox://styles/mapbox/satellite-streets-v12' }
  ];
  const [currentMapStyleId, setCurrentMapStyleId] = useState(mapStyles[1].id);

  // 首页不展示定位/指挥类全局提示；进入时清掉其他页残留的 antd message
  useEffect(() => {
    message.destroy();
  }, []);

  const reloadHomepageSettings = useCallback(() => {
    // 防缓存；带 token 时后端会按所属组织合并队伍品牌
    return fetch(`/api/admin/homepage-settings/public?_=${Date.now()}`, {
      headers: (() => {
        const token = localStorage.getItem('token');
        return token
          ? { Authorization: `Bearer ${token}`, 'Cache-Control': 'no-cache' }
          : { 'Cache-Control': 'no-cache' };
      })()
    })
      .then((res) => res.json())
      .then((data) => {
        const merged = normalizeHomepageSettings(
          data?.success && data.settings ? data.settings : DEFAULT_HOMEPAGE_SETTINGS
        );
        console.log('🏠 首页设置已加载:', merged);
        homepageSettingsRef.current = merged;
        setHomepageSettings(merged);
      })
      .catch(() => {
        homepageSettingsRef.current = DEFAULT_HOMEPAGE_SETTINGS;
        setHomepageSettings(DEFAULT_HOMEPAGE_SETTINGS);
      });
  }, []);

  useEffect(() => {
    reloadHomepageSettings();
    const onShow = () => {
      if (document.visibilityState === 'visible') reloadHomepageSettings();
    };
    window.addEventListener('focus', reloadHomepageSettings);
    document.addEventListener('visibilitychange', onShow);
    return () => {
      window.removeEventListener('focus', reloadHomepageSettings);
      document.removeEventListener('visibilitychange', onShow);
    };
  }, [reloadHomepageSettings]);

  const getActiveDeviceSettings = useCallback(() => {
    const mobile = isHomepageMobileView();
    return pickHomepageDeviceSettings(homepageSettingsRef.current, mobile);
  }, []);

  const applyEarthFit = useCallback(() => {
    const mobile = isHomepageMobileView();
    setIsMobileView(mobile);
    const deviceSettings = pickHomepageDeviceSettings(homepageSettingsRef.current, mobile);
    const metrics = getEarthFitMetrics(mapContainer.current, deviceSettings, mobile);
    const { zoom, diameter, textWidth } = metrics;
    earthZoomRef.current = zoom;
    setEarthDiameterPx(diameter);
    setBrandTextWidthPx(textWidth);
    setBrandFontSize(`${calcBrandFontSizePx(deviceSettings, diameter, textWidth)}px`);
    setBrandScale(1);
    console.log('🌍 地球适配', {
      mobile,
      margin: deviceSettings.earthMarginPercent,
      autoScale: deviceSettings.brandAutoScale,
      zoom,
      diameter,
      textWidth
    });

    if (mapRef.current && isEarthSpinningRef.current) {
      const center = mapRef.current.getCenter();
      mapRef.current.jumpTo({
        center: [center.lng, EARTH_LAT],
        zoom
      });
      mapRef.current.resize();
    }
  }, []);

  // 仅在文字仍超出目标宽度时略缩，保证完整可见
  const fitBrandTextToBox = useCallback(() => {
    const textEl = brandTextRef.current;
    const boxEl = brandBoxRef.current;
    if (!textEl || !boxEl) return;
    const avail = boxEl.clientWidth;
    if (avail <= 0) return;
    textEl.style.transform = 'scale(1)';
    const need = textEl.scrollWidth;
    if (need > avail * 1.02) {
      setBrandScale(Math.max(0.4, avail / need));
    } else {
      setBrandScale(1);
    }
  }, []);

  const startEarthSpin = useCallback(() => {
    if (!mapRef.current || isEarthSpinningRef.current) return;
    isEarthSpinningRef.current = true;
    applyEarthFit();

    const degreesPerSecond = 1.8;
    let lastTime = 0;

    const animate = (currentTime) => {
      if (!mapRef.current || !isEarthSpinningRef.current) return;

      if (!lastTime) {
        lastTime = currentTime;
        earthAnimationRef.current = requestAnimationFrame(animate);
        return;
      }

      const deltaTime = Math.min(currentTime - lastTime, 48);
      lastTime = currentTime;

      const center = mapRef.current.getCenter();
      let nextLng = center.lng + degreesPerSecond * (deltaTime / 1000);
      if (nextLng > 180) nextLng -= 360;
      if (nextLng < -180) nextLng += 360;

      mapRef.current.jumpTo({
        center: [nextLng, EARTH_LAT],
        zoom: earthZoomRef.current
      });

      earthAnimationRef.current = requestAnimationFrame(animate);
    };

    earthAnimationRef.current = requestAnimationFrame(animate);
  }, [applyEarthFit]);

  const stopEarthSpin = useCallback(() => {
    isEarthSpinningRef.current = false;
    if (earthAnimationRef.current) {
      cancelAnimationFrame(earthAnimationRef.current);
      earthAnimationRef.current = null;
    }
  }, []);

  const flyToCurrentLocation = useCallback(() => {
    if (!mapRef.current || locating) return;

    stopEarthSpin();
    setShowBrandTitle(false);
    setLocating(true);

    // 首页静默飞行，不弹 antd 提示（指挥页 locate 提示保留在 Dashboard）
    const flyTo = (lng, lat) => {
      if (!mapRef.current) return;
      mapRef.current.flyTo({
        center: [lng, lat],
        zoom: 14,
        duration: 3200,
        essential: true,
        easing: (t) => t * (2 - t)
      });
      mapStateManager.saveMapState('homepage', {
        center: [lng, lat],
        zoom: 14,
        style: currentMapStyleId
      });
      setLocating(false);
    };

    if (!navigator.geolocation) {
      flyTo(LONGQUAN_CENTER[0], LONGQUAN_CENTER[1]);
      return;
    }

    if (!isSecureGeolocationContext()) {
      // 保留控制台说明即可，界面不弹 toast
      if (getGeolocationHttpsHint()) {
        console.warn(getGeolocationHttpsHint());
      }
      flyTo(LONGQUAN_CENTER[0], LONGQUAN_CENTER[1]);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        flyTo(pos.coords.longitude, pos.coords.latitude);
      },
      () => {
        flyTo(LONGQUAN_CENTER[0], LONGQUAN_CENTER[1]);
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 60000 }
    );
  }, [currentMapStyleId, locating, stopEarthSpin]);

  const handleBrandClick = () => {
    if (!isEarthReady || locating) return;
    flyToCurrentLocation();
  };

  const switchMapStyle = (styleId) => {
    if (mapRef.current) {
      mapRef.current.setStyle(styleId);
      mapRef.current.once('style.load', () => {
        applyHomepageGlobeLook(mapRef.current);
        if (showBrandTitle && !isEarthSpinningRef.current) {
          const deviceSettings = getActiveDeviceSettings();
          const mobile = isHomepageMobileView();
          const { zoom } = getEarthFitMetrics(mapContainer.current, deviceSettings, mobile);
          earthZoomRef.current = zoom;
          mapRef.current.jumpTo({ center: [0, EARTH_LAT], zoom });
          startEarthSpin();
        }
      });
    }
    setCurrentMapStyleId(styleId);
  };

  function estimateViewHeight(zoom, lat, screenHeightPx = 800) {
    const R = 6378137;
    const initialResolution = (2 * Math.PI * R) / 256;
    const resolution = initialResolution / Math.pow(2, zoom);
    return Math.round((resolution * screenHeightPx) / 2);
  }

  async function fetchElevation(lng, lat) {
    try {
      const res = await fetch(`https://api.open-elevation.com/api/v1/lookup?locations=${lat},${lng}`);
      const data = await res.json();
      if (data?.results?.[0] && typeof data.results[0].elevation === 'number') {
        return Math.round(data.results[0].elevation);
      }
    } catch (e) {
      console.error('获取海拔失败:', e);
    }
    return null;
  }

  useEffect(() => {
    if (!homepageSettings || mapRef.current || !mapContainer.current) return undefined;

    const defaultState = mapStateManager.getDefaultMapState();
    const mobile = isHomepageMobileView();
    const deviceSettings = pickHomepageDeviceSettings(homepageSettings, mobile);
    const spinFirst = deviceSettings.showSpinningEarthFirst !== false;
    const { zoom: initialZoom, diameter, textWidth } = spinFirst
      ? getEarthFitMetrics(mapContainer.current, deviceSettings, mobile)
      : { zoom: 14, diameter: 280, textWidth: 280 };

    earthZoomRef.current = spinFirst ? initialZoom : 1.4;
    setEarthDiameterPx(diameter);
    setBrandTextWidthPx(textWidth);
    setBrandFontSize(`${calcBrandFontSizePx(deviceSettings, diameter, textWidth)}px`);
    setIsMobileView(mobile);

    mapRef.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: defaultState.style || mapStyles[1].id,
      center: spinFirst ? [0, EARTH_LAT] : LONGQUAN_CENTER,
      zoom: initialZoom,
      attributionControl: false
    });
    // 首页地球只用 Mapbox 样式（WGS84）：显式声明，不沿用指挥页底图状态
    attachCoordAdapter(mapRef.current, { datum: DATUM_WGS84, basemapId: 'homepage-mapbox' });

    setCurrentMapStyleId(defaultState.style || mapStyles[1].id);

    mapRef.current.on('load', () => {
      applyHomepageGlobeLook(mapRef.current);

      setIsEarthReady(true);

      if (spinFirst) {
        applyEarthFit();
        startEarthSpin();
        setShowBrandTitle(true);
      } else if (!locateTriggeredRef.current) {
        locateTriggeredRef.current = true;
        setShowBrandTitle(false);
        setTimeout(() => flyToCurrentLocation(), 80);
      }
    });

    mapRef.current.on('moveend', () => {
      if (isEarthSpinningRef.current) return;
      const center = mapRef.current.getCenter();
      const zoom = mapRef.current.getZoom();
      mapStateManager.saveMapState('homepage', {
        center: [center.lng, center.lat],
        zoom,
        style: currentMapStyleId
      });
    });

    return () => {
      stopEarthSpin();
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [homepageSettings]);

  useEffect(() => {
    const onResize = () => {
      if (!showBrandTitle) {
        if (mapRef.current) mapRef.current.resize();
        return;
      }
      applyEarthFit();
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [showBrandTitle, applyEarthFit]);

  // 大字显示后按实际渲染宽度再缩一次，避免裁切
  useEffect(() => {
    if (!showBrandTitle || !isEarthReady) return undefined;
    const timer = window.setTimeout(() => {
      fitBrandTextToBox();
    }, 40);
    return () => window.clearTimeout(timer);
  }, [
    showBrandTitle,
    isEarthReady,
    brandFontSize,
    earthDiameterPx,
    homepageSettings,
    isMobileView,
    fitBrandTextToBox
  ]);

  useEffect(() => {
    if (!mapRef.current) return undefined;

    const updateMapStatus = () => {
      if (!mapRef.current) return;
      const zoom = mapRef.current.getZoom();
      const center = mapRef.current.getCenter();
      const height = estimateViewHeight(zoom, center.lat, mapContainer.current?.offsetHeight || 800);
      setMapStatus((prev) => ({ ...prev, zoom, center: [center.lng, center.lat], height }));
      if (!isEarthSpinningRef.current) {
        fetchElevation(center.lng, center.lat).then((elevation) => {
          setMapStatus((prev) => ({ ...prev, elevation }));
        });
      }
    };

    mapRef.current.on('moveend', updateMapStatus);
    mapRef.current.on('zoomend', updateMapStatus);
    updateMapStatus();

    return () => {
      if (mapRef.current) {
        mapRef.current.off('moveend', updateMapStatus);
        mapRef.current.off('zoomend', updateMapStatus);
      }
    };
  }, [isEarthReady]);

  useEffect(() => {
    if (!mapRef.current) return undefined;
    const timer = setTimeout(() => {
      document.querySelectorAll('.mapboxgl-ctrl-attrib, .mapboxgl-ctrl-logo').forEach((el) => {
        el.style.display = 'none';
      });
    }, 500);
    return () => clearTimeout(timer);
  }, [isEarthReady]);

  useEffect(() => {
    const checkTitleBarState = () => {
      if (mapRef.current) {
        setTimeout(() => {
          if (mapRef.current) {
            mapRef.current.resize();
            if (isEarthSpinningRef.current) applyEarthFit();
          }
        }, 100);
      }
    };
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          checkTitleBarState();
        }
      });
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, [applyEarthFit]);

  const deviceSettings = pickHomepageDeviceSettings(
    homepageSettings || DEFAULT_HOMEPAGE_SETTINGS,
    isMobileView
  );
  const brandColor = hexToRgba(deviceSettings.brandColor, deviceSettings.brandOpacity);
  const letterSpacing = `${Number(deviceSettings.brandLetterSpacing) || (isMobileView ? 0.08 : 0.22)}em`;
  // 文字容器按目标文字宽度；可宽于地球（倍率>1）
  const brandBoxSize = Math.max(80, Math.round(brandTextWidthPx || earthDiameterPx));

  const containerStyle = {
    width: '100vw',
    height: 'calc(100vh - var(--header-total-height))',
    overflow: 'hidden',
    background: '#0a1628',
    position: 'relative',
    margin: 0,
    padding: 0
  };

  return (
    <div style={containerStyle}>
      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
        <div
          ref={mapContainer}
          style={{
            width: '100%',
            height: '100%'
          }}
        />

        {/* 大字叠在与地球同尺寸的居中方框内，保证相对地球居中且不溢出 */}
        {showBrandTitle && isEarthReady && (
          <div
            onClick={handleBrandClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') handleBrandClick();
            }}
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 30,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: locating ? 'wait' : 'pointer',
              userSelect: 'none',
              background: 'transparent'
            }}
          >
            <div
              ref={brandBoxRef}
              style={{
                width: brandBoxSize,
                maxWidth: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
                boxSizing: 'border-box',
                overflow: 'visible'
              }}
            >
              <div
                ref={brandTextRef}
                style={{
                  fontSize: brandFontSize,
                  fontWeight: 800,
                  letterSpacing,
                  color: brandColor,
                  textShadow: '0 6px 36px rgba(0,0,0,0.4)',
                  lineHeight: 1.1,
                  whiteSpace: 'nowrap',
                  textAlign: 'center',
                  transform: `scale(${brandScale})`,
                  transformOrigin: 'center center',
                  fontFamily: '"PingFang SC", "Microsoft YaHei", "Noto Sans SC", sans-serif'
                }}
              >
                {deviceSettings.brandText || '中国龙泉'}
              </div>
            </div>
          </div>
        )}

        {!showBrandTitle && isEarthReady && (
          <div style={{
            position: 'absolute',
            top: 18,
            left: 18,
            zIndex: 10,
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap'
          }}>
            {mapStyles.map((style) => (
              <button
                key={style.id}
                onClick={() => switchMapStyle(style.id)}
                style={{
                  background: currentMapStyleId === style.id ? '#1976d2' : '#fff',
                  color: currentMapStyleId === style.id ? '#fff' : '#1976d2',
                  border: '1px solid #1976d2',
                  borderRadius: 6,
                  padding: '4px 14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: 14
                }}
              >
                {style.name}
              </button>
            ))}
          </div>
        )}

        {!showBrandTitle && isEarthReady && (
          <div style={{
            position: 'absolute',
            left: 18,
            bottom: isMobileView ? 80 : 18,
            zIndex: 20,
            background: 'rgba(255,255,255,0.95)',
            borderRadius: 8,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            padding: '6px 12px',
            fontSize: 12,
            color: '#222',
            maxWidth: 220,
            wordBreak: 'break-all'
          }}>
            <div><b>缩放级别：</b>{mapStatus.zoom.toFixed(2)}</div>
            <div><b>离地高度：</b>{mapStatus.height ? `${mapStatus.height} 米` : '计算中...'}</div>
            <div><b>中心海拔：</b>{mapStatus.elevation != null ? `${mapStatus.elevation} 米` : '查询中...'}</div>
          </div>
        )}
      </div>
    </div>
  );
}

export default HomePage;
